import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { icons, layouts, textblocks, forms } from 'seal-ui-components';
import styled from 'styled-components';
import { AUTHENTICATION } from '@Constants/types';
import getSealVersion from '@Actions/sealAPIActions';
import { getLang, getDateTime } from '@Utils/dateUtils';
import { whichBrowser } from '@Utils/commonUtils';
import { setInStorage, getFromStorage, STORAGE_TYPE } from '@Service/storageService';
import store from '@Store';
import environment from '../../../../environment/version';
import style from './SettingsFooter.style';
import { calculateDialogStartPositionFromElement } from '@Utils/domUtils';

const SettingsFooter = ({ className, setDialogAnimationStartDimension }) => {
    const { isAuthenticated, version = '', user, isVersionMocked, releaseVersion } = useSelector(
        state => state.auth
    );

    const [mockSealVersion, setMockSealVersion] = useState(version);
    const [showMockField, setShowMockField] = useState(false);

    const revealMockVersionField = () => {
        if (
            user.username &&
            (user.username.toLowerCase() === 'root' || user.username.toLowerCase() === 'testuser')
        ) {
            setShowMockField(true);
        }
    };

    const applyMockVersion = event => {
        if (event.key === 'Enter') {
            if (!mockSealVersion) {
                setInStorage('mockSealVersion', '', STORAGE_TYPE.LOCAL_STORAGE);
                store.dispatch({ type: AUTHENTICATION.UN_MOCK_SEAL_VERSION });

                store.dispatch({
                    type: AUTHENTICATION.SET_SEAL_VERSION,
                    payload: releaseVersion
                });

                setShowMockField(false);
                setMockSealVersion('');

                getSealVersion();
            } else {
                const reg = /[0-9]+[.][0-9]+[.]+[0-9]+/;
                if (reg.test(mockSealVersion)) {
                    setInStorage('mockSealVersion', mockSealVersion, STORAGE_TYPE.LOCAL_STORAGE);
                    store.dispatch({
                        type: AUTHENTICATION.SET_MOCK_VERSION,
                        payload: mockSealVersion
                    });

                    setShowMockField(false);
                }
            }
        }
    };

    const hideMockField = () => setShowMockField(false);

    const updateMockVersion = e => setMockSealVersion(e.target.value);

    const { notes } = environment;

    const lastVersion = getFromStorage('lastVersion', STORAGE_TYPE.LOCAL_STORAGE);
    const currentVersion = environment.version !== '{BUILD_VERSION}' ? environment.version : 'DEV';

    useEffect(() => {
        if (notes && notes.length > 0 && lastVersion !== currentVersion) {
            document.querySelector('.release-link').click();
        }
    }, []);

    const releaseDialogOnClick = event => {
        setDialogAnimationStartDimension(calculateDialogStartPositionFromElement(event.target));
        store.dispatch({
            type: AUTHENTICATION.TOGGLE_RELEASE_DIALOG
        });
    };

    const buildNumber = environment.version !== '{BUILD_VERSION}' ? environment.version : 'DEV';

    const buildDatetime =
        environment.build_time !== '{BUILD_TIME}'
            ? getDateTime(environment.build_time, getLang())
            : 'Unknown';

    const sealVersion = isVersionMocked ? `${mockSealVersion} (mocked)` : version || 'Unknown';

    return (
        <div className={className}>
            {isAuthenticated && (
                <layouts.HorizontalBox className="user-info">
                    <icons.UserAvatarLargeIcon className="avatar" />
                    <textblocks.Text>
                        {user.email === 'root@company.com' ? 'root' : user.email}
                    </textblocks.Text>
                </layouts.HorizontalBox>
            )}
            <layouts.HorizontalBox className="user-info">
                <icons.NotesMediumIcon />
                <button className="release-link" onClick={releaseDialogOnClick}>
                    {"What's new"}
                </button>
            </layouts.HorizontalBox>
            <layouts.VerticalBoxDivider />
            <div className="app-version" onDoubleClick={revealMockVersionField}>
                <layouts.HorizontalBox>
                    <textblocks.Label>Version:</textblocks.Label>
                    <textblocks.Value>
                        {buildNumber} on {whichBrowser()}
                    </textblocks.Value>
                </layouts.HorizontalBox>

                {sealVersion !== 'Unknown' ? (
                    <layouts.HorizontalBox>
                        <textblocks.Label>Server:</textblocks.Label>
                        <textblocks.Value id="seal-version">{sealVersion}</textblocks.Value>
                    </layouts.HorizontalBox>
                ) : (
                    <layouts.HorizontalBox>
                        <textblocks.Label>Build Date:</textblocks.Label>
                        <textblocks.Value>{buildDatetime}</textblocks.Value>
                    </layouts.HorizontalBox>
                )}

                {showMockField && (
                    <forms.TextField
                        placeholder="v.v.v.v"
                        name="seal-version-mock"
                        onChange={updateMockVersion}
                        onKeyPress={applyMockVersion}
                        onBlur={hideMockField}
                        value={mockSealVersion}
                    />
                )}
            </div>
        </div>
    );
};

export default styled(SettingsFooter)`
    ${style};
`;
